/**
 * G2eGente - Dashboard de Gaps de Capacitação
 * Painel gerencial com visão consolidada dos gaps de treinamento
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { skillsMatrixAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Loader2,
  AlertTriangle,
  Users,
  GraduationCap,
  Building2,
  TrendingDown,
  DollarSign,
  Shield,
  ChevronRight,
  Clock,
  XCircle,
  BarChart3,
  Target,
  FileText,
  Info,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardGaps() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await skillsMatrixAPI.getGapsDashboard();
      setData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = data?.stats || {};
  const conformidadeColor = stats.conformidade_geral >= 80 ? 'text-green-600' : 
                           stats.conformidade_geral >= 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="gaps-dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard de Gaps</h1>
            <p className="text-slate-600">Visão gerencial dos gaps de capacitação da equipe</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/company/matriz-polivalencia')}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Matriz de Polivalência
            </Button>
            <Button 
              onClick={() => navigate('/company/treinamentos')}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Gerenciar Treinamentos
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className={stats.conformidade_geral < 70 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stats.conformidade_geral >= 80 ? 'bg-green-100' : stats.conformidade_geral >= 60 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                    <Target className={`w-5 h-5 ${conformidadeColor}`} />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${conformidadeColor}`}>
                      {stats.conformidade_geral}%
                    </p>
                    <p className="text-sm text-slate-600">Conformidade Geral</p>
                  </div>
                </div>
              </div>
              <Progress value={stats.conformidade_geral} className="mt-3" />
            </CardContent>
          </Card>

          <Card className={stats.total_gaps > 0 ? 'border-amber-200 bg-amber-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.total_gaps}</p>
                  <p className="text-sm text-slate-600">Gaps de Treinamento</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={stats.treinamentos_criticos_com_gap > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.treinamentos_criticos_com_gap}</p>
                  <p className="text-sm text-slate-600">NRs com Pendências</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(stats.custo_estimado_total)}
                  </p>
                  <p className="text-sm text-slate-600">Custo Est. para Fechar Gaps</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Diagnóstico de Vinculação de Cargos */}
        {data?.diagnostico_vinculacao && (data.diagnostico_vinculacao.funcionarios_sem_vinculo > 0 || data.diagnostico_vinculacao.cargos_sem_codigo > 0) && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Link2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-blue-800">Diagnóstico de Vinculação</h3>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Info</Badge>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                    <div className="bg-white/80 rounded-lg p-2">
                      <p className="text-slate-500">Funcionários vinculados</p>
                      <p className="font-semibold text-green-600">{data.diagnostico_vinculacao.funcionarios_vinculados} de {data.diagnostico_vinculacao.funcionarios_vinculados + data.diagnostico_vinculacao.funcionarios_sem_vinculo}</p>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2">
                      <p className="text-slate-500">Sem código de cargo</p>
                      <p className="font-semibold text-amber-600">{data.diagnostico_vinculacao.funcionarios_sem_codigo_cargo}</p>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2">
                      <p className="text-slate-500">Cargos sem código</p>
                      <p className="font-semibold text-amber-600">{data.diagnostico_vinculacao.cargos_sem_codigo} de {data.diagnostico_vinculacao.total_cargos}</p>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2">
                      <p className="text-slate-500">Sem vínculo</p>
                      <p className="font-semibold text-red-600">{data.diagnostico_vinculacao.funcionarios_sem_vinculo}</p>
                    </div>
                  </div>
                  {data.diagnostico_vinculacao.aviso && (
                    <div className="mt-3 p-2 bg-white/60 rounded-lg flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">{data.diagnostico_vinculacao.aviso}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* NRs Críticas - Alerta especial */}
        {data?.treinamentos_criticos_nrs?.length > 0 && (
          <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Shield className="w-5 h-5" />
                Treinamentos Regulatórios Críticos (NRs)
              </CardTitle>
              <CardDescription>
                Treinamentos obrigatórios por lei com funcionários pendentes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.treinamentos_criticos_nrs.map((t) => (
                  <div key={t.training_id} className="p-3 bg-white rounded-lg border border-red-200 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className="bg-red-100 text-red-700 mb-1">{t.requisitos_legais}</Badge>
                        <p className="font-medium text-slate-900 text-sm">{t.nome}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">{t.gap_total}</p>
                        <p className="text-xs text-slate-500">pendentes</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                      <span>{t.total_requeridos} funcionários requerem</span>
                      <span className={t.conformidade < 70 ? 'text-red-600 font-medium' : ''}>{t.conformidade}% conformidade</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs com análises detalhadas */}
        <Tabs defaultValue="treinamentos" className="space-y-4">
          <TabsList>
            <TabsTrigger value="treinamentos" className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Por Treinamento
            </TabsTrigger>
            <TabsTrigger value="funcionarios" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Por Funcionário
            </TabsTrigger>
            <TabsTrigger value="setores" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Por Setor
            </TabsTrigger>
          </TabsList>

          {/* Tab: Por Treinamento */}
          <TabsContent value="treinamentos">
            <Card>
              <CardHeader>
                <CardTitle>Gaps por Treinamento</CardTitle>
                <CardDescription>
                  Treinamentos com maior número de funcionários pendentes
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Treinamento</TableHead>
                      <TableHead className="text-center">Categoria</TableHead>
                      <TableHead className="text-center">Requeridos</TableHead>
                      <TableHead className="text-center">Válidos</TableHead>
                      <TableHead className="text-center">Pendentes</TableHead>
                      <TableHead className="text-center">Vencidos</TableHead>
                      <TableHead className="text-center">Conformidade</TableHead>
                      <TableHead className="text-right">Custo Est.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.gaps_por_treinamento?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          Nenhum gap de treinamento identificado
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.gaps_por_treinamento?.map((t) => (
                        <TableRow key={t.training_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{t.nome}</p>
                              {t.requisitos_legais && (
                                <Badge variant="outline" className="text-red-600 text-xs mt-1">
                                  {t.requisitos_legais}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{t.categoria}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {t.total_requeridos}
                          </TableCell>
                          <TableCell className="text-center text-green-600">
                            {t.treinados_valido}
                          </TableCell>
                          <TableCell className="text-center">
                            {t.nao_treinados > 0 && (
                              <span className="text-amber-600 font-medium">{t.nao_treinados}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {t.treinados_vencido > 0 && (
                              <span className="text-red-600 font-medium">{t.treinados_vencido}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Progress value={t.conformidade} className="w-16 h-2" />
                              <span className={`text-sm font-medium ${
                                t.conformidade >= 80 ? 'text-green-600' : 
                                t.conformidade >= 60 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {t.conformidade}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-slate-600">
                            {t.custo_estimado_gap > 0 ? formatCurrency(t.custo_estimado_gap) : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Por Funcionário */}
          <TabsContent value="funcionarios">
            <Card>
              <CardHeader>
                <CardTitle>Funcionários com Mais Pendências</CardTitle>
                <CardDescription>
                  Funcionários que precisam de mais atenção em capacitação
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Setor</TableHead>
                      <TableHead className="text-center">Requisitos</TableHead>
                      <TableHead className="text-center">Faltantes</TableHead>
                      <TableHead className="text-center">Vencidos</TableHead>
                      <TableHead className="text-center">Vencendo</TableHead>
                      <TableHead className="text-center">Conformidade</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.funcionarios_criticos?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Nenhum funcionário com pendências
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.funcionarios_criticos?.map((f) => (
                        <TableRow key={f.employee_id}>
                          <TableCell className="font-medium">{f.nome}</TableCell>
                          <TableCell>{f.cargo}</TableCell>
                          <TableCell>{f.setor || '-'}</TableCell>
                          <TableCell className="text-center">{f.total_requisitos}</TableCell>
                          <TableCell className="text-center">
                            {f.treinamentos_faltantes > 0 && (
                              <Badge className="bg-amber-100 text-amber-700">{f.treinamentos_faltantes}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {f.treinamentos_vencidos > 0 && (
                              <Badge className="bg-red-100 text-red-700">{f.treinamentos_vencidos}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {f.treinamentos_vencendo > 0 && (
                              <Badge className="bg-yellow-100 text-yellow-700">{f.treinamentos_vencendo}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`font-medium ${
                              f.conformidade >= 80 ? 'text-green-600' : 
                              f.conformidade >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {f.conformidade}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate('/company/matriz-polivalencia')}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Por Setor */}
          <TabsContent value="setores">
            <Card>
              <CardHeader>
                <CardTitle>Conformidade por Setor</CardTitle>
                <CardDescription>
                  Visão da conformidade de treinamentos por departamento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data?.gaps_por_setor?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Nenhum setor cadastrado
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data?.gaps_por_setor?.map((setor) => (
                      <div key={setor.setor} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">{setor.setor}</p>
                              <p className="text-sm text-slate-500">
                                {setor.total_funcionarios} funcionários • {setor.total_requisitos} requisitos
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${
                              setor.conformidade >= 80 ? 'text-green-600' : 
                              setor.conformidade >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {setor.conformidade}%
                            </p>
                            {setor.total_gaps > 0 && (
                              <p className="text-sm text-slate-500">{setor.total_gaps} gaps</p>
                            )}
                          </div>
                        </div>
                        <Progress 
                          value={setor.conformidade} 
                          className={`h-3 ${
                            setor.conformidade < 60 ? '[&>div]:bg-red-500' : 
                            setor.conformidade < 80 ? '[&>div]:bg-yellow-500' : ''
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Ações Sugeridas */}
        {(stats.total_gaps > 0 || stats.treinamentos_criticos_com_gap > 0) && (
          <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-900">
                <FileText className="w-5 h-5" />
                Ações Recomendadas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {stats.treinamentos_criticos_com_gap > 0 && (
                  <div className="p-3 bg-white rounded-lg border shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Shield className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Priorizar NRs</p>
                        <p className="text-sm text-slate-600">
                          {stats.treinamentos_criticos_com_gap} treinamentos regulatórios com gaps
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {data?.funcionarios_criticos?.length > 0 && (
                  <div className="p-3 bg-white rounded-lg border shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Users className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Gerar PDIs</p>
                        <p className="text-sm text-slate-600">
                          Criar planos de desenvolvimento para {Math.min(data.funcionarios_criticos.length, 5)} funcionários críticos
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-white rounded-lg border shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-cyan-100 rounded-lg">
                      <TrendingDown className="w-4 h-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Acompanhar</p>
                      <p className="text-sm text-slate-600">
                        Monitorar semanalmente a evolução da conformidade
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
